import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useSession } from './session';
import type {
  Alert as AlertRecord,
  Camera,
  Detection,
  IdentityProfile,
  IdentityVerification,
  LivenessSession,
  Subject,
  Tenant,
  TenantProductType,
  TenantDirectoryUser,
  WebhookEndpoint,
} from './types';
import { getFrontendEnvironmentSummary } from '../lib/api/platformApi';
import LoginScreen from '../features/auth/LoginScreen';
import BrandLogo from '../components/BrandLogo';
import DashboardPage from '../features/dashboard/DashboardPage';
import TenantsPage from '../features/tenants/TenantsPage';
import CamerasPage from '../features/cameras/CamerasPage';
import WatchlistPage from '../features/watchlist/WatchlistPage';
import AlertsPage from '../features/alerts/AlertsPage';
import DetectionsPage from '../features/detections/DetectionsPage';
import WebhooksPage from '../features/webhooks/WebhooksPage';
import AdminPage from '../features/admin/AdminPage';
import IdentityProfilesPage from '../features/identity/IdentityProfilesPage';
import VerifyIdentityPage from '../features/identity/VerifyIdentityPage';
import TenantUsersPage from '../features/tenant-users/TenantUsersPage';
import LivenessPage from '../features/liveness/LivenessPage';
import {
  addSubjectFace,
  compareLivenessSessionFace,
  createCamera,
  createIdentityProfile,
  createTenantUser,
  createIdentityVerification,
  createLivenessSession,
  createSubject,
  createTenant,
  createUploadUrl,
  createWebhook,
  deleteLivenessSession,
  deleteIdentityProfile,
  getTenant,
  getIdentityReferencePhotoUrl,
  getIdentityVerificationProbePhotoUrl,
  listAlerts,
  listCameras,
  listDetections,
  listIdentityProfiles,
  listIdentityVerifications,
  listLivenessSessions,
  listTenantUsers,
  listSubjects,
  listSubjectFaces,
  listTenants,
  listWebhooks,
  resendTenantUserInvite,
  resetTenantUserPassword,
  reviewIdentityVerification,
  refreshLivenessSession,
  testWebhook,
  uploadIdentityReferencePhoto,
  updateTenantUser,
  updateIdentityProfile,
  updateTenantSettings,
  uploadSnapshot,
} from '../lib/api/platformApi';

type PageKey =
  | 'dashboard'
  | 'tenants'
  | 'cameras'
  | 'watchlist'
  | 'tenantUsers'
  | 'identityProfiles'
  | 'identityVerifications'
  | 'liveness'
  | 'alerts'
  | 'detections'
  | 'webhooks'
  | 'admin';

const drawerWidth = 260;
const UI_STATE_KEY = 'crime-platform-ui-state';

const pageConfig: Array<{ key: PageKey; title: string }> = [
  { key: 'dashboard', title: 'Overview' },
  { key: 'admin', title: 'Tenant Settings' },
  { key: 'tenantUsers', title: 'Tenant Users' },
  { key: 'identityProfiles', title: 'Identity Profiles' },
  { key: 'identityVerifications', title: 'Verify Identity' },
  { key: 'liveness', title: 'Sessions' },
  { key: 'tenants', title: 'Tenants' },
  { key: 'cameras', title: 'Cameras' },
  { key: 'watchlist', title: 'Watchlist' },
  { key: 'alerts', title: 'Alerts' },
  { key: 'detections', title: 'Detections' },
  { key: 'webhooks', title: 'Webhooks' },
];

const identityPages: PageKey[] = ['identityProfiles', 'identityVerifications'];
const alertingPages: PageKey[] = ['cameras', 'watchlist', 'alerts', 'detections', 'webhooks'];
const livenessPages: PageKey[] = ['liveness'];
const topKeys: PageKey[] = ['dashboard', 'tenants', 'admin', 'tenantUsers'];

function isPageAllowedForProduct(page: PageKey, productType: TenantProductType | null): boolean {
  if (!productType) {
    return !identityPages.includes(page) && !alertingPages.includes(page);
  }
  if (productType === 'identity') {
    return !alertingPages.includes(page) && !livenessPages.includes(page);
  }
  if (productType === 'alerting') {
    return !identityPages.includes(page) && !livenessPages.includes(page);
  }
  return !identityPages.includes(page) && !alertingPages.includes(page);
}

function isPageCompatibleWithTenantProduct(page: PageKey, productType: TenantProductType | null): boolean {
  if (!productType) {
    return true;
  }
  if (identityPages.includes(page)) {
    return productType === 'identity';
  }
  if (alertingPages.includes(page)) {
    return productType === 'alerting';
  }
  if (livenessPages.includes(page)) {
    return productType === 'liveness';
  }
  return true;
}

export default function AppShell() {
  const {
    principal,
    authConfig,
    authBootstrapped,
    authError,
    authHeaders,
    signOut,
    sessionExpiresAt,
    sessionWillExpireSoon,
    refreshSession,
  } = useSession();
  const [page, setPage] = useState<PageKey>('dashboard');
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [activeTenant, setActiveTenant] = useState<Tenant | null>(null);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [tenantUsers, setTenantUsers] = useState<TenantDirectoryUser[]>([]);
  const [identityProfiles, setIdentityProfiles] = useState<IdentityProfile[]>([]);
  const [identityVerifications, setIdentityVerifications] = useState<IdentityVerification[]>([]);
  const [livenessSessions, setLivenessSessions] = useState<LivenessSession[]>([]);
  const [alerts, setAlerts] = useState<AlertRecord[]>([]);
  const [detections, setDetections] = useState<Detection[]>([]);
  const [webhooks, setWebhooks] = useState<WebhookEndpoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState('');
  const [warningDismissedUntil, setWarningDismissedUntil] = useState(0);
  const [refreshingAuthSession, setRefreshingAuthSession] = useState(false);

  const environment = getFrontendEnvironmentSummary();
  const currentTenantProduct = activeTenant?.productType ?? null;
  const effectiveTenantId =
    selectedTenantId || (principal && principal.role !== 'platform-admin' ? principal.tenantId : '');

  useEffect(() => {
    if (!principal) {
      return;
    }
    const raw = localStorage.getItem(UI_STATE_KEY);
    if (!raw) {
      return;
    }
    try {
      const saved = JSON.parse(raw) as { page?: PageKey; selectedTenantId?: string };
      if (saved.selectedTenantId && principal.role === 'platform-admin') {
        setSelectedTenantId(saved.selectedTenantId);
      }
      if (saved.page) {
        setPage(saved.page);
      }
    } catch {
      // Ignore invalid persisted UI states.
    }
  // Restore once per authenticated principal.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [principal?.subject]);

  useEffect(() => {
    if (!principal) {
      return;
    }
    localStorage.setItem(
      UI_STATE_KEY,
      JSON.stringify({
        page,
        selectedTenantId: principal.role === 'platform-admin' ? selectedTenantId : principal.tenantId,
      })
    );
  }, [page, principal, selectedTenantId]);

  useEffect(() => {
    if (!principal) {
      return;
    }

    if (!selectedTenantId && principal.role !== 'platform-admin') {
      setSelectedTenantId(principal.tenantId);
    }
  }, [principal, selectedTenantId]);

  useEffect(() => {
    if (!principal) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setDataError('');
      try {
        const shouldLoadTenantUsers = principal.role === 'platform-admin' || principal.role === 'tenant-admin';
        const tenantList = await listTenants(authHeaders);
        const tenantScope = tenantList.items;

        const resolvedTenantId =
          effectiveTenantId ||
          (principal.role === 'platform-admin' ? (tenantScope[0]?.tenantId ?? '') : principal.tenantId);

        if (!cancelled) {
          setTenants(tenantScope);
          if (resolvedTenantId && resolvedTenantId !== selectedTenantId) {
            setSelectedTenantId(resolvedTenantId);
          }
        }

        if (!resolvedTenantId) {
          if (!cancelled) {
            setActiveTenant(null);
            setCameras([]);
            setSubjects([]);
            setTenantUsers([]);
            setIdentityProfiles([]);
            setIdentityVerifications([]);
            setLivenessSessions([]);
            setAlerts([]);
            setDetections([]);
            setWebhooks([]);
          }
          return;
        }

        const tenantRecord =
          tenantScope.find((tenant) => tenant.tenantId === resolvedTenantId) ??
          (await getTenant(authHeaders, resolvedTenantId));

        // Avoid API Gateway burst throttling by loading dashboard datasets in a
        // paced sequence instead of a single high-fan-out Promise.all.
        const tenantUserResponse = shouldLoadTenantUsers
          ? await listTenantUsers(authHeaders, resolvedTenantId)
          : { items: [] as TenantDirectoryUser[] };
        const isIdentityTenant = tenantRecord.productType === 'identity';
        const isAlertingTenant = tenantRecord.productType === 'alerting';
        const isLivenessTenant = tenantRecord.productType === 'liveness';
        const subjectResponse = isAlertingTenant
          ? await listSubjects(authHeaders, resolvedTenantId)
          : { items: [] as Subject[] };
        const identityProfileResponse = isIdentityTenant
          ? await listIdentityProfiles(authHeaders, resolvedTenantId)
          : { items: [] as IdentityProfile[] };
        const identityVerificationResponse = isIdentityTenant
          ? await listIdentityVerifications(authHeaders, resolvedTenantId)
          : { items: [] as IdentityVerification[] };
        const livenessSessionResponse = isLivenessTenant && authConfig?.enableLiveness
          ? await listLivenessSessions(authHeaders, resolvedTenantId)
          : { items: [] as LivenessSession[] };
        const alertResponse = isAlertingTenant
          ? await listAlerts(authHeaders, resolvedTenantId)
          : { items: [] as AlertRecord[] };
        const detectionResponse = isAlertingTenant
          ? await listDetections(authHeaders, resolvedTenantId)
          : { items: [] as Detection[] };
        const webhookResponse = isAlertingTenant
          ? await listWebhooks(authHeaders, resolvedTenantId)
          : { items: [] as WebhookEndpoint[] };
        const cameraResponse = isAlertingTenant
          ? await listCameras(authHeaders, resolvedTenantId)
          : { items: [] as Camera[] };

        if (!cancelled) {
          setActiveTenant(tenantRecord);
          setCameras(cameraResponse.items);
          setSubjects(subjectResponse.items);
          setTenantUsers(tenantUserResponse.items);
          setIdentityProfiles(identityProfileResponse.items);
          setIdentityVerifications(identityVerificationResponse.items);
          setLivenessSessions(livenessSessionResponse.items);
          setAlerts(alertResponse.items);
          setDetections(detectionResponse.items);
          setWebhooks(webhookResponse.items);
        }
      } catch (error) {
        if (!cancelled) {
          setDataError(error instanceof Error ? error.message : 'Failed to load console data.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [authConfig?.enableLiveness, authHeaders, effectiveTenantId, principal, selectedTenantId]);

  const canManageTenants = principal?.role === 'platform-admin';
  const canManageTenantUsers = principal?.role === 'platform-admin' || principal?.role === 'tenant-admin';
  const showExpiryWarning =
    Boolean(principal)
    && sessionWillExpireSoon
    && Date.now() > warningDismissedUntil;

  const refreshScope = useCallback(async () => {
    if (!principal || !effectiveTenantId) {
      return;
    }

    setSelectedTenantId(effectiveTenantId);
    const tenantList = await listTenants(authHeaders);
    setTenants(tenantList.items);
    const tenantRecord =
      tenantList.items.find((tenant) => tenant.tenantId === effectiveTenantId) ??
      (await getTenant(authHeaders, effectiveTenantId));
    const isIdentityTenant = tenantRecord.productType === 'identity';
    const isAlertingTenant = tenantRecord.productType === 'alerting';
    const isLivenessTenant = tenantRecord.productType === 'liveness';
    setActiveTenant(tenantRecord);
    if (isAlertingTenant) {
      setCameras((await listCameras(authHeaders, effectiveTenantId)).items);
      setSubjects((await listSubjects(authHeaders, effectiveTenantId)).items);
      setAlerts((await listAlerts(authHeaders, effectiveTenantId)).items);
      setDetections((await listDetections(authHeaders, effectiveTenantId)).items);
      setWebhooks((await listWebhooks(authHeaders, effectiveTenantId)).items);
    } else {
      setCameras([]);
      setSubjects([]);
      setAlerts([]);
      setDetections([]);
      setWebhooks([]);
    }
    if (isLivenessTenant && authConfig?.enableLiveness) {
      setLivenessSessions((await listLivenessSessions(authHeaders, effectiveTenantId)).items);
    } else {
      setLivenessSessions([]);
    }
    if (principal.role === 'platform-admin' || principal.role === 'tenant-admin') {
      setTenantUsers((await listTenantUsers(authHeaders, effectiveTenantId)).items);
    } else {
      setTenantUsers([]);
    }
    if (isIdentityTenant) {
      setIdentityProfiles((await listIdentityProfiles(authHeaders, effectiveTenantId)).items);
      setIdentityVerifications((await listIdentityVerifications(authHeaders, effectiveTenantId)).items);
    } else {
      setIdentityProfiles([]);
      setIdentityVerifications([]);
    }
  }, [authConfig?.enableLiveness, authHeaders, effectiveTenantId, principal]);

  useEffect(() => {
    const onLivenessCompleted = (event: MessageEvent) => {
      const payload = event.data as { type?: string } | null;
      if (payload?.type !== 'privaface.liveness.completed') {
        return;
      }
      void refreshScope();
    };

    window.addEventListener('message', onLivenessCompleted);
    return () => {
      window.removeEventListener('message', onLivenessCompleted);
    };
  }, [refreshScope]);

  useEffect(() => {
    const isLivenessPage = page === 'liveness';
    const isLivenessTenant = activeTenant?.productType === 'liveness';
    const hasOpenSessions = livenessSessions.some((session) => session.status === 'created' || session.status === 'in_progress');
    if (!isLivenessPage || !authConfig?.enableLiveness || !isLivenessTenant || !hasOpenSessions) {
      return;
    }

    let cancelled = false;
    const intervalId = window.setInterval(() => {
      void (async () => {
        try {
          const response = await listLivenessSessions(authHeaders, effectiveTenantId);
          if (!cancelled) {
            setLivenessSessions(response.items);
          }
        } catch {
          // Ignore transient polling errors to avoid disrupting the operator workflow.
        }
      })();
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeTenant?.productType, authConfig?.enableLiveness, authHeaders, effectiveTenantId, livenessSessions, page]);

  const visiblePages = useMemo(
    () =>
      pageConfig.filter((item) => {
        if (item.key === 'tenants') {
          return canManageTenants;
        }
        if (item.key === 'tenantUsers') {
          return canManageTenantUsers;
        }
        if (livenessPages.includes(item.key) && !authConfig?.enableLiveness) {
          return false;
        }
        if (principal?.role === 'platform-admin') {
          return true;
        }
        return isPageAllowedForProduct(item.key, currentTenantProduct);
      }),
    [authConfig?.enableLiveness, canManageTenantUsers, canManageTenants, currentTenantProduct, principal?.role]
  );
  const topPages = visiblePages.filter((item) => topKeys.includes(item.key));
  const visibleIdentityPages = visiblePages.filter((item) => identityPages.includes(item.key));
  const secondaryPages = visiblePages.filter((item) => !topKeys.includes(item.key) && !identityPages.includes(item.key));
  const visibleLivenessPages = secondaryPages.filter((item) => livenessPages.includes(item.key));
  const preAlertingPages = secondaryPages.filter((item) => !alertingPages.includes(item.key) && !livenessPages.includes(item.key));
  const visibleAlertingPages = secondaryPages.filter((item) => alertingPages.includes(item.key));

  useEffect(() => {
    if (!visiblePages.some((item) => item.key === page)) {
      setPage('dashboard');
    }
  }, [page, visiblePages]);

  const pageProductCompatible = isPageCompatibleWithTenantProduct(page, currentTenantProduct);

  if (!authBootstrapped) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography>Bootstrapping console...</Typography>
        </Stack>
      </Box>
    );
  }

  if (!principal) {
    return <LoginScreen />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#eef2f4' }}>
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{ width: `calc(100% - ${drawerWidth}px)`, ml: `${drawerWidth}px`, backdropFilter: 'blur(12px)' }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <BrandLogo variant="icon" height={32} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {visiblePages.find((item) => item.key === page)?.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tenant scope: {effectiveTenantId}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip label={`Stage: ${environment.stage}`} />
            <Chip label={principal.role} color="primary" variant="outlined" />
            {currentTenantProduct ? <Chip label={`Product: ${currentTenantProduct}`} color="secondary" variant="outlined" /> : null}
            <Button variant="outlined" onClick={() => void refreshScope()}>
              Refresh
            </Button>
            <Button variant="contained" onClick={signOut}>
              Sign Out
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid rgba(15, 23, 42, 0.08)',
            background: 'linear-gradient(180deg, #12222f 0%, #1a3440 100%)',
            color: '#f8fafc',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ p: 3 }}>
          <BrandLogo variant="full" height={44} />
          <Typography variant="body2" sx={{ opacity: 0.72, mt: 1 }}>
            Operator console for watchlists, alerts, and identity verification.
          </Typography>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <List>
          {topPages.map((item) => (
            <ListItemButton
              key={item.key}
              selected={page === item.key}
              onClick={() => setPage(item.key)}
              sx={{
                mx: 1,
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255,255,255,0.12)',
                },
              }}
            >
              <ListItemText primary={item.title} />
            </ListItemButton>
          ))}
          {visibleIdentityPages.length > 0 ? <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.14)' }} /> : null}
          {visibleIdentityPages.length > 0 ? (
            <Typography
              variant="subtitle2"
              sx={{ px: 3, py: 0.75, opacity: 0.9, fontWeight: 800, letterSpacing: '0.03em' }}
            >
              Identity:
            </Typography>
          ) : null}
          {visibleIdentityPages.map((item) => (
            <ListItemButton
              key={item.key}
              selected={page === item.key}
              onClick={() => setPage(item.key)}
              sx={{
                mx: 1,
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255,255,255,0.12)',
                },
              }}
            >
              <ListItemText primary={item.title} />
            </ListItemButton>
          ))}
          {secondaryPages.length > 0 ? <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.14)' }} /> : null}
          {preAlertingPages.map((item) => (
            <ListItemButton
              key={item.key}
              selected={page === item.key}
              onClick={() => setPage(item.key)}
              sx={{
                mx: 1,
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255,255,255,0.12)',
                },
              }}
            >
              <ListItemText primary={item.title} />
            </ListItemButton>
          ))}
          {visibleLivenessPages.length > 0 ? (
            <Typography
              variant="subtitle2"
              sx={{ px: 3, py: 0.75, opacity: 0.9, fontWeight: 800, letterSpacing: '0.03em' }}
            >
              Liveness:
            </Typography>
          ) : null}
          {visibleLivenessPages.map((item) => (
            <ListItemButton
              key={item.key}
              selected={page === item.key}
              onClick={() => setPage(item.key)}
              sx={{
                mx: 1,
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255,255,255,0.12)',
                },
              }}
            >
              <ListItemText primary={item.title} />
            </ListItemButton>
          ))}
          {visibleLivenessPages.length > 0 && visibleAlertingPages.length > 0 ? (
            <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.14)' }} />
          ) : null}
          {visibleAlertingPages.length > 0 ? (
            <Typography
              variant="subtitle2"
              sx={{ px: 3, py: 0.75, opacity: 0.9, fontWeight: 800, letterSpacing: '0.03em' }}
            >
              Alerting:
            </Typography>
          ) : null}
          {visibleAlertingPages.map((item) => (
            <ListItemButton
              key={item.key}
              selected={page === item.key}
              onClick={() => setPage(item.key)}
              sx={{
                mx: 1,
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255,255,255,0.12)',
                },
              }}
            >
              <ListItemText primary={item.title} />
            </ListItemButton>
          ))}
        </List>
        <Box sx={{ p: 3, mt: 'auto' }}>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            {principal.email || principal.subject}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.55 }}>
            API: {environment.apiBaseUrl || 'same-origin'}
          </Typography>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 4, ml: `${drawerWidth}px`, mt: 10 }}>
        <Container maxWidth="xl">
          <Stack spacing={3}>
            {showExpiryWarning ? (
              <Alert
                severity="warning"
                action={(
                  <Stack direction="row" spacing={1}>
                    <Button
                      color="inherit"
                      size="small"
                      disabled={refreshingAuthSession}
                      onClick={() => {
                        void (async () => {
                          setRefreshingAuthSession(true);
                          try {
                            const refreshed = await refreshSession();
                            if (refreshed) {
                              setWarningDismissedUntil(0);
                            }
                          } finally {
                            setRefreshingAuthSession(false);
                          }
                        })();
                      }}
                    >
                      {refreshingAuthSession ? 'Refreshing...' : 'Continue Session'}
                    </Button>
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => setWarningDismissedUntil(Date.now() + 60_000)}
                    >
                      Later
                    </Button>
                  </Stack>
                )}
              >
                Session expires soon{sessionExpiresAt ? ` (${new Date(sessionExpiresAt).toLocaleTimeString()})` : ''}. This can happen due to inactivity or maximum session lifetime.
              </Alert>
            ) : null}
            {authError ? <Alert severity="warning">{authError}</Alert> : null}
            {dataError ? <Alert severity="error">{dataError}</Alert> : null}
            {!pageProductCompatible && effectiveTenantId ? (
              <Alert severity="info">
                The selected tenant has product <strong>{currentTenantProduct}</strong>. Switch tenant to use this page.
              </Alert>
            ) : null}
            {loading ? (
              <Box sx={{ py: 10, display: 'grid', placeItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              pageProductCompatible
                ? renderPage({
                    page,
                    principal,
                    activeTenant,
                    tenants,
                    effectiveTenantId,
                    cameras,
                    subjects,
                    tenantUsers,
                    identityProfiles,
                    identityVerifications,
                    livenessSessions,
                    alerts,
                    detections,
                    webhooks,
                    setSelectedTenantId,
                    authHeaders,
                    refreshScope,
                  })
                : null
            )}
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

interface RenderPageProps {
  readonly page: PageKey;
  readonly principal: NonNullable<ReturnType<typeof useSession>['principal']>;
  readonly activeTenant: Tenant | null;
  readonly tenants: Tenant[];
  readonly effectiveTenantId: string;
  readonly cameras: Camera[];
  readonly subjects: Subject[];
  readonly tenantUsers: TenantDirectoryUser[];
  readonly identityProfiles: IdentityProfile[];
  readonly identityVerifications: IdentityVerification[];
  readonly livenessSessions: LivenessSession[];
  readonly alerts: AlertRecord[];
  readonly detections: Detection[];
  readonly webhooks: WebhookEndpoint[];
  readonly setSelectedTenantId: (tenantId: string) => void;
  readonly authHeaders: Record<string, string>;
  readonly refreshScope: () => Promise<void>;
}

function renderPage({
  page,
  principal,
  activeTenant,
  tenants,
  effectiveTenantId,
  cameras,
  subjects,
  tenantUsers,
  identityProfiles,
  identityVerifications,
  livenessSessions,
  alerts,
  detections,
  webhooks,
  setSelectedTenantId,
  authHeaders,
  refreshScope,
}: RenderPageProps) {
  switch (page) {
    case 'dashboard':
      return (
        <DashboardPage
          tenant={activeTenant}
          cameras={cameras}
          subjects={subjects}
          identityProfiles={identityProfiles}
          identityVerifications={identityVerifications}
          alerts={alerts}
          detections={detections}
          webhooks={webhooks}
        />
      );
    case 'tenants':
      return (
        <TenantsPage
          principal={principal}
          tenants={tenants}
          selectedTenantId={effectiveTenantId}
          onSelectTenant={setSelectedTenantId}
          onCreateTenant={async ({ displayName, productType }) => {
            await createTenant(authHeaders, {
              displayName,
              productType,
            });
            await refreshScope();
          }}
        />
      );
    case 'cameras':
      return (
        <CamerasPage
          tenantId={effectiveTenantId}
          cameras={cameras}
          onCreateCamera={async (payload) => {
            await createCamera(authHeaders, payload);
            await refreshScope();
          }}
          onUploadSnapshot={async (cameraId, file) => {
            const presignedUpload = await createUploadUrl(authHeaders, {
              tenantId: effectiveTenantId,
              cameraId,
              contentType: file.type || 'image/jpeg',
            });
            await uploadSnapshot(presignedUpload, file);
          }}
        />
      );
    case 'watchlist':
      return (
        <WatchlistPage
          tenantId={effectiveTenantId}
          subjects={subjects}
          onCreateSubject={async (payload) => {
            const subject = await createSubject(authHeaders, payload);
            await refreshScope();
            return subject;
          }}
          onAddFace={async (subjectId, payload) => {
            await addSubjectFace(authHeaders, subjectId, payload);
            await refreshScope();
          }}
          onGetSubjectFaces={async (subjectId) => listSubjectFaces(authHeaders, effectiveTenantId, subjectId)}
        />
      );
    case 'tenantUsers':
      return (
        <TenantUsersPage
          principal={principal}
          tenantId={effectiveTenantId}
          tenants={tenants}
          selectedTenantId={effectiveTenantId}
          onSelectTenant={setSelectedTenantId}
          users={tenantUsers}
          onCreateUser={async (payload) => {
            await createTenantUser(authHeaders, payload);
            await refreshScope();
          }}
          onUpdateUser={async (username, payload) => {
            await updateTenantUser(authHeaders, username, payload);
            await refreshScope();
          }}
          onResetPassword={async (username, targetTenantId) => {
            await resetTenantUserPassword(authHeaders, username, targetTenantId);
          }}
          onResendInvite={async (username, targetTenantId) => {
            await resendTenantUserInvite(authHeaders, username, targetTenantId);
          }}
        />
      );
    case 'identityProfiles':
      return (
        <IdentityProfilesPage
          tenantId={effectiveTenantId}
          profiles={identityProfiles}
          onCreateProfile={async (payload) => {
            await createIdentityProfile(authHeaders, payload);
            await refreshScope();
          }}
          onUpdateProfile={async (profileId, payload) => {
            await updateIdentityProfile(authHeaders, profileId, payload);
            await refreshScope();
          }}
          onDeleteProfile={async (profileId, tenantId) => {
            await deleteIdentityProfile(authHeaders, profileId, tenantId);
            await refreshScope();
          }}
          onUploadReferencePhoto={async (profileId, payload) => {
            await uploadIdentityReferencePhoto(authHeaders, profileId, payload);
            await refreshScope();
          }}
          onGetReferencePhotoUrl={async (profileId, tenantId) => {
            const result = await getIdentityReferencePhotoUrl(authHeaders, tenantId, profileId);
            return result.url;
          }}
        />
      );
    case 'identityVerifications':
      return (
        <VerifyIdentityPage
          tenantId={effectiveTenantId}
          verifications={identityVerifications}
          onVerify={async (payload) => {
            const verification = await createIdentityVerification(authHeaders, payload);
            await refreshScope();
            return verification;
          }}
          onReview={async (verificationId, payload) => {
            await reviewIdentityVerification(authHeaders, verificationId, payload);
            await refreshScope();
          }}
          onGetProbePhotoUrl={async (verificationId, tenantId) => {
            const result = await getIdentityVerificationProbePhotoUrl(authHeaders, tenantId, verificationId);
            return result.url;
          }}
        />
      );
    case 'liveness':
      return (
        <LivenessPage
          tenantId={effectiveTenantId}
          sessions={livenessSessions}
          onCreateSession={async () => {
            await createLivenessSession(authHeaders, { tenantId: effectiveTenantId });
            await refreshScope();
          }}
          onRefreshSession={async (sessionId) => {
            await refreshLivenessSession(authHeaders, sessionId, { tenantId: effectiveTenantId });
            await refreshScope();
          }}
          onCompareSessionFace={async (sessionId, payload) => (
            compareLivenessSessionFace(authHeaders, sessionId, payload)
          )}
          onDeleteSession={async (sessionId) => {
            await deleteLivenessSession(authHeaders, sessionId, effectiveTenantId);
            await refreshScope();
          }}
        />
      );
    case 'alerts':
      return <AlertsPage alerts={alerts} />;
    case 'detections':
      return <DetectionsPage detections={detections} />;
    case 'webhooks':
      return (
        <WebhooksPage
          tenantId={effectiveTenantId}
          webhooks={webhooks}
          onCreateWebhook={async (payload) => {
            await createWebhook(authHeaders, payload);
            await refreshScope();
          }}
          onTestWebhook={async (webhookId) => {
            await testWebhook(authHeaders, webhookId, effectiveTenantId);
          }}
        />
      );
    case 'admin':
      return (
        <AdminPage
          tenant={activeTenant}
          onSave={async (payload) => {
            await updateTenantSettings(authHeaders, effectiveTenantId, payload);
            await refreshScope();
          }}
        />
      );
    default:
      return null;
  }
}
